/**
 * Offline storage layer using IndexedDB (idb wrapper).
 *
 * Stores:
 *   - schedules    : cached scheduled services (key=id)
 *   - clients      : cached clients referenced in route (key=id)
 *   - reports      : locally created/synced reports (key=id)
 *   - outbox       : queued operations (auto-increment key)
 *   - meta         : key-value misc (key=key)
 */
import { openDB } from "idb";

const DB_NAME = "trulyn_offline";
const DB_VERSION = 2;

let dbPromise = null;

export function getDb() {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains("schedules")) {
                    const s = db.createObjectStore("schedules", { keyPath: "id" });
                    s.createIndex("by_date", "scheduled_date");
                }
                if (!db.objectStoreNames.contains("clients")) {
                    db.createObjectStore("clients", { keyPath: "id" });
                }
                if (!db.objectStoreNames.contains("reports")) {
                    db.createObjectStore("reports", { keyPath: "id" });
                }
                if (!db.objectStoreNames.contains("outbox")) {
                    const o = db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
                    o.createIndex("by_status", "status");
                    o.createIndex("by_schedule", "scheduleId");
                }
                if (!db.objectStoreNames.contains("meta")) {
                    db.createObjectStore("meta", { keyPath: "key" });
                }
                // v2: MIP photos captured offline, waiting to upload
                if (!db.objectStoreNames.contains("pendingPhotos")) {
                    const p = db.createObjectStore("pendingPhotos", { keyPath: "id" });
                    p.createIndex("by_service", "serviceId");
                }
            },
        });
    }
    return dbPromise;
}

// ---------- Schedules ----------
export async function cacheSchedules(list) {
    if (!Array.isArray(list) || list.length === 0) return;
    const db = await getDb();
    const tx = db.transaction("schedules", "readwrite");
    for (const s of list) await tx.store.put(s);
    await tx.done;
}

export async function getCachedSchedules({ date } = {}) {
    const db = await getDb();
    const all = await db.getAll("schedules");
    return date ? all.filter((s) => s.scheduled_date === date) : all;
}

export async function getCachedSchedule(id) {
    const db = await getDb();
    return db.get("schedules", id);
}

export async function updateCachedSchedule(id, patch) {
    const db = await getDb();
    const existing = await db.get("schedules", id);
    if (!existing) return null;
    const merged = { ...existing, ...patch, updated_at: new Date().toISOString() };
    await db.put("schedules", merged);
    return merged;
}

// ---------- Clients ----------
export async function cacheClients(list) {
    if (!Array.isArray(list) || list.length === 0) return;
    const db = await getDb();
    const tx = db.transaction("clients", "readwrite");
    for (const c of list) await tx.store.put(c);
    await tx.done;
}

export async function getCachedClients() {
    const db = await getDb();
    return db.getAll("clients");
}

// ---------- Reports ----------
export async function cacheReports(list) {
    if (!Array.isArray(list) || list.length === 0) return;
    const db = await getDb();
    const tx = db.transaction("reports", "readwrite");
    for (const r of list) await tx.store.put(r);
    await tx.done;
}

export async function getCachedReports() {
    const db = await getDb();
    return db.getAll("reports");
}

// ---------- Outbox ----------
// type: "start_service" | "not_done" | "complete_report"
// status: "pending" | "syncing" | "error" | "done"
export async function enqueueOutbox(item) {
    const db = await getDb();
    const record = {
        type: item.type,
        url: item.url,
        method: item.method || "POST",
        body: item.body,
        scheduleId: item.scheduleId,
        createdAt: new Date().toISOString(),
        attempts: 0,
        status: "pending",
        error: null,
    };
    const id = await db.add("outbox", record);
    return { ...record, id };
}

export async function listOutbox() {
    const db = await getDb();
    return db.getAll("outbox");
}

export async function countPending() {
    const db = await getDb();
    const tx = db.transaction("outbox", "readonly");
    const items = await tx.store.index("by_status").getAll("pending");
    const errs = await tx.store.index("by_status").getAll("error");
    await tx.done;
    return items.length + errs.length;
}

export async function updateOutbox(id, patch) {
    const db = await getDb();
    const item = await db.get("outbox", id);
    if (!item) return null;
    const merged = { ...item, ...patch };
    await db.put("outbox", merged);
    return merged;
}

export async function removeOutbox(id) {
    const db = await getDb();
    await db.delete("outbox", id);
}

export async function clearDoneOutbox() {
    const db = await getDb();
    const tx = db.transaction("outbox", "readwrite");
    const done = await tx.store.index("by_status").getAll("done");
    for (const it of done) await tx.store.delete(it.id);
    await tx.done;
}

export async function getOutboxForSchedule(scheduleId) {
    const db = await getDb();
    return db.getAllFromIndex("outbox", "by_schedule", scheduleId);
}

// ---------- Pending MIP photos (captured offline) ----------
export async function addPendingPhoto(record) {
    const db = await getDb();
    await db.put("pendingPhotos", record);
    return record;
}
export async function getPendingPhotos(serviceId) {
    const db = await getDb();
    if (serviceId) return db.getAllFromIndex("pendingPhotos", "by_service", serviceId);
    return db.getAll("pendingPhotos");
}
export async function updatePendingPhoto(id, patch) {
    const db = await getDb();
    const ex = await db.get("pendingPhotos", id);
    if (!ex) return null;
    const merged = { ...ex, ...patch };
    await db.put("pendingPhotos", merged);
    return merged;
}
export async function removePendingPhoto(id) {
    const db = await getDb();
    await db.delete("pendingPhotos", id);
}
export async function countPendingPhotos() {
    const db = await getDb();
    return (await db.getAll("pendingPhotos")).length;
}

// ---------- Meta ----------
export async function setMeta(key, value) {
    const db = await getDb();
    await db.put("meta", { key, value });
}
export async function getMeta(key) {
    const db = await getDb();
    const r = await db.get("meta", key);
    return r?.value;
}

// ---------- Reset (logout) ----------
export async function wipeLocal() {
    const db = await getDb();
    const tx = db.transaction(["schedules", "clients", "reports", "outbox", "meta", "pendingPhotos"], "readwrite");
    await Promise.all([
        tx.objectStore("schedules").clear(),
        tx.objectStore("clients").clear(),
        tx.objectStore("reports").clear(),
        tx.objectStore("outbox").clear(),
        tx.objectStore("meta").clear(),
        tx.objectStore("pendingPhotos").clear(),
    ]);
    await tx.done;
}
